var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { chipColumn, monoColumn, nameColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, listPager } from 'library/components/ListScreen';
import { Account as AccountResource } from 'constants/resources';
import localeTranslator from 'library/Hocs/LocalTranslator';
import services from 'services';
import AccountDrawer from './AccountDrawer';
var Accounts = function (props) {
    var _a, _b, _c, _d;
    var t = props.translate;
    var navigate = useNavigate();
    var location = useLocation();
    var accountService = services.accountService, accountTypeService = services.accountTypeService, permissionService = services.permissionService;
    var _e = useState(1), pageNumber = _e[0], setPageNumber = _e[1];
    var _f = useState(function () { var _a; return Boolean((_a = location.state) === null || _a === void 0 ? void 0 : _a.openNewAccount); }), drawerOpen = _f[0], setDrawerOpen = _f[1];
    var _g = useState(''), searchValue = _g[0], setSearchValue = _g[1];
    var _h = useState('all'), typeFilter = _h[0], setTypeFilter = _h[1];
    var debounceRef = useRef(undefined);
    var canAdd = permissionService.hasPermissionToAdd(AccountResource);
    var canFilterTypes = permissionService.hasAccountTypePermission();
    var accountTypes = accountTypeService.useAccountTypes(canFilterTypes).data;
    var filters = useMemo(function () {
        var next = {};
        var name = searchValue.trim();
        if (name)
            next.name = name;
        if (typeFilter !== 'all')
            next.account_type_id = typeFilter;
        return next;
    }, [searchValue, typeFilter]);
    var _j = accountService.useAccounts(pageNumber, filters), data = _j.data, isLoading = _j.isLoading;
    var accounts = (data === null || data === void 0 ? void 0 : data.accounts) || [];
    var total = Number(((_a = data === null || data === void 0 ? void 0 : data.meta) === null || _a === void 0 ? void 0 : _a.count) || 0);
    var from = Number(((_b = data === null || data === void 0 ? void 0 : data.meta) === null || _b === void 0 ? void 0 : _b.from) || 0);
    var to = Number(((_c = data === null || data === void 0 ? void 0 : data.meta) === null || _c === void 0 ? void 0 : _c.to) || 0);
    var pages = Math.max(1, Number(((_d = data === null || data === void 0 ? void 0 : data.meta) === null || _d === void 0 ? void 0 : _d.pages) || 1));
    var typeOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('allTypes') }
    ], (accountTypes || []).map(function (accountType) { return ({
        value: accountType.getId(),
        label: accountType.getName()
    }); }), true); }, [accountTypes, t]);
    useEffect(function () {
        var state = location.state;
        if (!(state === null || state === void 0 ? void 0 : state.openNewAccount))
            return;
        setDrawerOpen(true);
        navigate('/accounts', { replace: true, state: null });
    }, [location.state, navigate]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPageNumber(1);
            setSearchValue(value);
        }, 300);
    };
    var columns = useMemo(function () { return [
        monoColumn({ key: 'id', title: t('id'), width: '88px', value: function (account) { return account.getId(); } }),
        nameColumn({ key: 'name', title: t('name'), width: 'minmax(220px, 1.6fr)', value: function (account) { return account.getName(); } }),
        chipColumn({
            key: 'type',
            title: t('accountType'),
            width: 'minmax(180px, 1fr)',
            tone: 'is-sky',
            value: function (account) { var _a; return (_a = account.getAccountType()) === null || _a === void 0 ? void 0 : _a.getName(); }
        })
    ]; }, [t]);
    return (_jsxs(ListScreen, { title: t('header.accounts'), count: t('countBadge', { count: total }), subtitle: t('subtitle'), actions: canAdd ? (_jsx(ListAddButton, { onboarding: true, label: t('common:buttons.newAccount'), onClick: function () { return setDrawerOpen(true); } })) : undefined, search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, toolbarEnd: canFilterTypes ? (_jsx(Select, { className: "cidx-select", value: typeFilter, onChange: function (value) {
                setTypeFilter(value);
                setPageNumber(1);
            }, options: typeOptions, popupMatchSelectWidth: false })) : undefined, children: [_jsx(DataTable, { compact: true, minWidth: "560px", loading: isLoading, rows: accounts, rowKey: function (account) { return account.getId(); }, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                    total: total,
                    page: pageNumber,
                    pages: pages,
                    meta: t('showingRange', { from: from, to: to, total: total }),
                    previous: t('previous'),
                    next: t('next'),
                    center: t('pageOf', { page: pageNumber, pages: pages }),
                    setPage: setPageNumber
                }) }), _jsx(AccountDrawer, { visiable: drawerOpen, onCloseDrawer: function () { return setDrawerOpen(false); } })] }));
};
export default localeTranslator(Accounts, 'accounts');
